/* ============================================================
   Aicheapy theme.js — no dependencies, loaded with defer.
   Cell matrix, scroll reveals, count-up stats, sticky-header
   hairline, mobile menu, qty steppers, key reveal/copy, PDP thumbs.
   Motion respects prefers-reduced-motion.
   ============================================================ */
(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var hasIO = "IntersectionObserver" in window;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  ready(function () {
    initMatrix();
    initReveal();
    initCountUp();
    initStickyHeader();
    initMobileMenu();
    initQty();
    initKeyDelivery();
    initThumbs();
  });

  /* ---------- cell matrix ----------
     Paints lit cells onto <canvas data-matrix>, aligned to the hairline grid
     the parent .matrix draws in CSS (cell size and colours come from its
     --matrix-* custom properties). Cells fade in and out at random; the pointer
     leaves a short accent trail. Runs at ~30fps only while on screen and the
     tab is visible; under reduced motion it paints one static frame. */
  function initMatrix() {
    var canvases = document.querySelectorAll("canvas[data-matrix]");
    if (!canvases.length || !window.HTMLCanvasElement) return;
    canvases.forEach(function (c) { if (c.getContext) matrix(c); });
  }

  function matrix(canvas) {
    var ctx = canvas.getContext("2d");
    var host = canvas.parentNode;
    var zone = canvas.closest("[data-matrix-zone]") || host;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = 0, h = 0, cell = 32, ox = 0, cols = 0, rows = 0;
    var ink = "10,16,32", acc = "36,86,232", inkMax = 0.06, accMax = 0.2;
    var lit = [];           // { i, born, life, peak, accent }
    var busy = {};          // cell index -> true while lit
    var pointer = null, lastCell = -1;
    var visible = false, raf = 0, last = 0, spawnAcc = 0;

    function readVars() {
      var cs = getComputedStyle(host);
      cell = parseFloat(cs.getPropertyValue("--matrix-cell")) || 32;
      ink = (cs.getPropertyValue("--matrix-rgb") || ink).trim();
      acc = (cs.getPropertyValue("--matrix-accent-rgb") || acc).trim();
      inkMax = parseFloat(cs.getPropertyValue("--matrix-ink-max")) || inkMax;
      accMax = parseFloat(cs.getPropertyValue("--matrix-accent-max")) || accMax;
    }

    function resize() {
      readVars();
      w = host.clientWidth; h = host.clientHeight;
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // CSS positions the grid at 50% 0, i.e. the first tile starts at (w - cell) / 2.
      ox = (((w - cell) / 2) % cell + cell) % cell;
      cols = Math.ceil((w - ox) / cell) + 1;
      rows = Math.ceil(h / cell);
      lit = []; busy = {};
      if (reduced) seedStatic();
    }

    function light(i, now, accent, life) {
      if (busy[i] || i < 0) return;
      busy[i] = true;
      lit.push({ i: i, born: now, life: life, peak: 0.45 + Math.random() * 0.55, accent: accent });
    }

    function cellX(i) { return ox - cell + (i % cols) * cell; }
    function cellY(i) { return Math.floor(i / cols) * cell; }

    function draw(now) {
      ctx.clearRect(0, 0, w, h);
      var keep = [];
      for (var k = 0; k < lit.length; k++) {
        var c = lit[k];
        var p = reduced ? 0.5 : (now - c.born) / c.life;
        if (p >= 1) { delete busy[c.i]; continue; }
        keep.push(c);
        var a = Math.sin(p * Math.PI) * c.peak * (c.accent ? accMax : inkMax);
        ctx.fillStyle = "rgba(" + (c.accent ? acc : ink) + "," + a.toFixed(3) + ")";
        ctx.fillRect(cellX(c.i) + 1, cellY(c.i) + 1, cell - 1, cell - 1);
      }
      lit = keep;
    }

    function seedStatic() {
      var n = Math.round(cols * rows * 0.05);
      for (var k = 0; k < n; k++) light(Math.floor(Math.random() * cols * rows), 0, Math.random() < 0.3, 1);
      draw(0);
    }

    function tick(now) {
      raf = 0;
      if (!visible || document.hidden) return;
      if (now - last >= 33) {
        var dt = last ? Math.min(now - last, 100) : 33;
        last = now;
        // ~1 new cell per 2,500 cells per frame keeps density steady at any size
        spawnAcc += (cols * rows) / 2500 * (dt / 33) * 2.2;
        while (spawnAcc >= 1) {
          spawnAcc -= 1;
          var accent = Math.random() < 0.22;
          light(Math.floor(Math.random() * cols * rows), now, accent, 1600 + Math.random() * 2600);
        }
        draw(now);
      }
      raf = requestAnimationFrame(tick);
    }

    function start() { if (!raf && !reduced) { last = 0; raf = requestAnimationFrame(tick); } }

    resize();
    if ("ResizeObserver" in window) new ResizeObserver(function () { resize(); }).observe(host);
    else window.addEventListener("resize", resize);
    if (reduced) return;

    if (hasIO) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) start();
      }).observe(host);
    } else { visible = true; start(); }
    document.addEventListener("visibilitychange", function () { if (!document.hidden) start(); });

    // pointer trail — listens on the zone (the section), since the canvas
    // itself ignores pointer events so it never blocks clicks
    zone.addEventListener("pointermove", function (e) {
      if (e.pointerType === "touch") return;
      var r = host.getBoundingClientRect();
      var x = e.clientX - r.left, y = e.clientY - r.top;
      if (x < 0 || y < 0 || x > w || y > h) return;
      var col = Math.floor((x - ox + cell) / cell), row = Math.floor(y / cell);
      var i = row * cols + col;
      if (i === lastCell) return;
      lastCell = i;
      light(i, performance.now(), true, 900);
    }, { passive: true });
  }

  /* ---------- reveal on scroll ---------- */
  function initReveal() {
    var els = document.querySelectorAll("[data-reveal], [data-reveal-stagger]");
    if (!els.length) return;
    if (reduced || !hasIO) {
      els.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- count-up numbers (rAF, ~1s, one-shot) ---------- */
  function initCountUp() {
    var els = document.querySelectorAll("[data-count]");
    if (!els.length) return;

    function parse(text) {
      var m = text.match(/^([^\d]*)([\d.,]+)(.*)$/);
      if (!m) return null;
      var num = parseFloat(m[2].replace(/,/g, ""));
      if (isNaN(num)) return null;
      var dec = (m[2].split(".")[1] || "").length;
      var grouped = m[2].indexOf(",") !== -1;
      return { prefix: m[1], value: num, suffix: m[3], dec: dec, grouped: grouped };
    }

    function fmt(n, p) {
      var s = n.toFixed(p.dec);
      if (p.grouped) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return p.prefix + s + p.suffix;
    }

    function run(el) {
      var p = parse(el.textContent.trim());
      if (!p) return;
      var start = null, dur = 1000;
      function frame(ts) {
        if (!start) start = ts;
        var t = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - t, 3);
        el.textContent = fmt(p.value * eased, p);
        if (t < 1) requestAnimationFrame(frame);
        else el.textContent = fmt(p.value, p);
      }
      requestAnimationFrame(frame);
    }

    if (reduced || !hasIO) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { run(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- sticky header hairline ---------- */
  function initStickyHeader() {
    var header = document.querySelector(".site-header");
    if (!header) return;
    var ticking = false;
    function update() { header.classList.toggle("is-stuck", window.scrollY > 4); ticking = false; }
    window.addEventListener("scroll", function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- mobile menu ---------- */
  function initMobileMenu() {
    var toggle = document.querySelector("[data-menu-toggle]");
    var menu = document.querySelector("[data-mobile-menu]");
    if (!toggle || !menu) return;
    toggle.addEventListener("click", function () {
      var open = menu.classList.toggle("is-open");
      toggle.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }

  /* ---------- quantity steppers ---------- */
  function initQty() {
    document.querySelectorAll(".qty-stepper").forEach(function (s) {
      var input = s.querySelector("input[type='number']");
      if (!input) return;
      var min = parseInt(input.getAttribute("min"), 10); if (isNaN(min)) min = 1;
      var minus = s.querySelector(".qty-minus"), plus = s.querySelector(".qty-plus");
      function set(v) { input.value = v; input.dispatchEvent(new Event("change", { bubbles: true })); }
      if (minus) minus.addEventListener("click", function (e) { e.preventDefault(); set(Math.max(min, (parseInt(input.value, 10) || min) - 1)); });
      if (plus) plus.addEventListener("click", function (e) { e.preventDefault(); set((parseInt(input.value, 10) || min) + 1); });
    });
  }

  /* ---------- license key reveal + copy ---------- */
  function initKeyDelivery() {
    document.querySelectorAll("[data-key-reveal]").forEach(function (wrap) {
      var code = wrap.querySelector("[data-key-code]");
      if (!code) return;
      var revealBtn = wrap.querySelector("[data-key-reveal-button]");
      var copyBtn = wrap.querySelector("[data-key-copy]");
      var flash = wrap.querySelector("[data-key-copied]");
      var actual = code.getAttribute("data-key-value") || code.textContent.trim();

      if (revealBtn) revealBtn.addEventListener("click", function () {
        code.textContent = actual;
        code.classList.add("is-revealed");
        revealBtn.classList.add("hidden");
        if (copyBtn) copyBtn.classList.remove("hidden");
      });

      if (copyBtn) copyBtn.addEventListener("click", function () {
        copyText(actual).then(function (ok) {
          if (ok && flash) {
            flash.classList.add("is-visible");
            setTimeout(function () { flash.classList.remove("is-visible"); }, 1600);
          }
        });
      });
    });
  }

  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(function () { return true; }, function () { return false; });
    }
    return new Promise(function (resolve) {
      var ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      var ok = false; try { ok = document.execCommand("copy"); } catch (e) {}
      document.body.removeChild(ta); resolve(ok);
    });
  }

  /* ---------- PDP thumbnails ---------- */
  function initThumbs() {
    var main = document.querySelector(".main-product__image");
    var thumbs = document.querySelectorAll(".main-product__thumb");
    if (!main || !thumbs.length) return;
    thumbs.forEach(function (t) {
      t.addEventListener("click", function () {
        var src = t.getAttribute("data-thumb"); if (!src) return;
        main.setAttribute("src", src); main.removeAttribute("srcset");
        thumbs.forEach(function (x) { x.classList.remove("is-active"); });
        t.classList.add("is-active");
      });
    });
  }
})();
